import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { Bell, BellRing, Check, Search, ChevronLeft, LogOut, User as UserIcon, Megaphone, Volume2, VolumeX } from 'lucide-react';
import { Badge } from '../ui/badge';
import { WhatsNewPopover } from '../whatsNew/WhatsNewPopover';
import { DataHealthAlert } from './DataHealthAlert';
import { DeploymentRegressionAlert } from './DeploymentRegressionAlert';
import { BulkJobIndicator } from './BulkJobIndicator';
import { AppNotificationsPanel } from './AppNotificationsPanel';
import { ChatbotAlertBell } from './ChatbotAlertBell';
import { isBellMuted, setBellMuted, playBell } from '../../lib/chime';

const LEAD_TYPES = ['lead_claimable', 'lead_assigned', 'lead_new', 'lead'];

export const TopBar = ({ title, onMenuClick, isMobile, onLogout }) => {
  const { currentUser, notifications, markNotifRead, markAllRead } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAppNotif, setShowAppNotif] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [muted, setMuted] = useState(isBellMuted());
  const isAdmin = ['admin', 'sub_admin', 'business_head'].includes(currentUser?.role);
  const unreadCount = notifications.filter(n => !n.read).length;
  const unreadLeads = notifications.filter(n => !n.read && LEAD_TYPES.includes(n.notif_type)).length;

  React.useEffect(() => {
    const open = () => setShowAppNotif(true);
    window.addEventListener('open-app-notifications', open);
    let timer;
    const ring = () => { setRinging(true); clearTimeout(timer); timer = setTimeout(() => setRinging(false), 4000); };
    window.addEventListener('lead-bell', ring);
    const onMute = (e) => setMuted(!!e.detail?.muted);
    window.addEventListener('lead-bell-mute', onMute);
    return () => {
      window.removeEventListener('open-app-notifications', open);
      window.removeEventListener('lead-bell', ring);
      window.removeEventListener('lead-bell-mute', onMute);
      clearTimeout(timer);
    };
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setBellMuted(next);
    setMuted(next);
    if (!next) playBell();
  };

  return (
    <header data-testid="top-bar" className="h-14 sm:h-16 bg-white border-b border-border/60 flex items-center justify-between px-3 sm:px-6 md:px-8 sticky top-0 z-40">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        {isMobile && (
          <button data-testid="mobile-back-home" onClick={() => navigate('/home')} className="flex items-center gap-1 pl-1 pr-2.5 py-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors flex-shrink-0">
            <ChevronLeft className="w-4 h-4" />
            <span className="text-[13px] font-bold">Home</span>
          </button>
        )}
        <h2 className="text-base sm:text-xl font-bold text-slate-900 truncate">{title}</h2>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 flex-shrink-0">
        {/* Search */}
        <div className="relative hidden md:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="global-search"
            type="text"
            placeholder="Search..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400/20 outline-none transition-all w-60"
          />
        </div>

        {/* What's New (admin-only) */}
        <WhatsNewPopover />

        {/* Bulk-trace live indicator — shows for any user with a running or
            queued inventory job. Click → Processed Records. */}
        <BulkJobIndicator />

        {/* Data Health alert (admin-only) — surfaces error/warn counts */}
        <DataHealthAlert />

        {/* Deployment regression alert (admin-only) — flashes red when a marked
            release shows MORE unhealthy integrations than the previous one */}
        <DeploymentRegressionAlert />

        {/* Live-agent request bell (Support Chat users) */}
        <ChatbotAlertBell />

        {/* Notification Bell */}
        <div className="relative">
          <button
            data-testid="notification-bell"
            onClick={() => setShowNotifs(!showNotifs)}
            className={`relative p-2 rounded-lg hover:bg-slate-50 transition-colors ${ringing ? 'bell-ringing' : ''}`}
            title={unreadLeads > 0 ? `${unreadLeads} new lead alert${unreadLeads === 1 ? '' : 's'}` : 'Notifications'}
          >
            {ringing || unreadLeads > 0
              ? <BellRing className={`w-5 h-5 ${ringing ? 'text-amber-600' : 'text-slate-700'}`} strokeWidth={1.75} />
              : <Bell className="w-5 h-5 text-slate-600" strokeWidth={1.5} />}
            {unreadCount > 0 && (
              unreadLeads > 0
                ? <span className="notification-count" data-testid="notification-lead-count">{unreadLeads > 99 ? '99+' : unreadLeads}</span>
                : <span className="notification-dot" />
            )}
          </button>

          {showNotifs && (
            <div data-testid="notification-panel" className="absolute right-0 top-12 w-80 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-50">
              <div className="p-4 border-b border-border/40 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="lead-bell-mute-toggle"
                    onClick={toggleMute}
                    title={muted ? 'Lead alert sound is OFF — click to turn on' : 'Lead alert sound is ON — click to mute'}
                    className={`p-1 rounded-md transition-colors ${muted ? 'text-slate-400 hover:text-slate-600' : 'text-amber-600 hover:text-amber-700'}`}
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  {unreadCount > 0 && (
                    <button
                      data-testid="mark-all-read-btn"
                      onClick={markAllRead}
                      className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="p-4 text-sm text-slate-400 text-center">No notifications</p>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      data-testid={`notification-${n.id}`}
                      className={`p-3 border-b border-border/20 flex items-start gap-3 hover:bg-slate-50 transition-colors cursor-pointer ${!n.read ? 'bg-amber-50/40' : ''}`}
                      onClick={() => !n.read && markNotifRead(n.id)}
                    >
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-amber-500' : 'bg-slate-200'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{n.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{n.notif_type}</Badge>
                        </div>
                      </div>
                      {!n.read && (
                        <Check className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User */}
        {currentUser && (
          <div className="relative">
            <button
              data-testid="user-info"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 pl-3 border-l border-border/40 hover:bg-slate-50 rounded-lg p-1.5 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                {currentUser.name?.charAt(0)}
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-sm font-semibold text-slate-900 leading-none">{currentUser.name}</p>
                <p className="text-[11px] text-slate-400">{ROLE_LABELS[currentUser.role]}</p>
              </div>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-12 w-48 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-50">
                <div className="p-3 border-b border-border/40">
                  <p className="text-sm font-semibold text-slate-900">{currentUser.name}</p>
                  <p className="text-xs text-slate-400">{currentUser.email}</p>
                </div>
                <Link
                  to="/profile"
                  data-testid="profile-link"
                  onClick={() => setShowUserMenu(false)}
                  className="w-full p-3 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors border-b border-border/40"
                >
                  <UserIcon className="w-4 h-4" />
                  My Profile
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => {
                    setShowUserMenu(false);
                    onLogout && onLogout();
                  }}
                  className="w-full p-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
